import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { BookOpen, Calendar, Mail, ArrowRight } from 'lucide-react';

const Teaching: React.FC = () => {
  const { t } = useTranslation();

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="max-w-6xl mx-auto"
    >
      {/* Header Section */}
      <div className="text-center mb-12">
        <motion.h1 
          className="font-display text-5xl md:text-7xl font-semibold text-stone-800 mb-6 leading-tight pb-4"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          {t('teaching.title')}
        </motion.h1>
        <motion.p 
          className="text-xl text-stone-600 max-w-3xl mx-auto leading-relaxed"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          {t('teaching.subtitle')}
        </motion.p>
      </div>

      {/* Colles Section */}
      <motion.div 
        className="bg-[rgba(255,251,246,0.82)] backdrop-blur-sm rounded-[1.75rem] p-8 mb-12 shadow-[0_20px_60px_rgba(31,41,55,0.08)] border border-stone-200/80"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.6 }}
      >
        <div className="text-center mb-8">
          <h2 className="font-display text-4xl font-semibold text-stone-800 mb-4">{t('teaching.colles.title')}</h2>
          <p className="text-lg text-stone-600 max-w-3xl mx-auto">
            {t('teaching.colles.subtitle')}
          </p>
        </div>

        {/* Explanation */}
        <div className="max-w-4xl mx-auto mb-8">
          <p className="text-lg text-stone-700 leading-relaxed">
            {t('teaching.colles.explanation')}
          </p>
        </div>

        {/* Academic Years */}
        <div className="grid md:grid-cols-2 gap-8 mb-8">
          {/* 2023-2024 */}
          <motion.div 
            className="rounded-2xl border border-[#d7d0d9] bg-[linear-gradient(135deg,rgba(250,247,250,0.95),rgba(240,232,240,0.92))] p-8 shadow-xl hover:shadow-2xl transition-all duration-300"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            whileHover={{ scale: 1.02 }}
          >
            <div className="flex items-center mb-6">
              <Calendar className="w-8 h-8 text-[#735d78] mr-3" />
              <h3 className="font-display text-3xl font-semibold text-stone-800">2023-2024</h3>
            </div>
            <p className="text-stone-600 mb-6">
              {t('teaching.colles.academicYear')} 2023-2024
            </p>
            <Link 
              to="/teaching/2023-2024"
              className="inline-flex items-center px-6 py-3 bg-[#735d78] text-white rounded-lg hover:bg-[#614c66] transition-colors duration-200 font-medium"
            >
              <BookOpen className="w-5 h-5 mr-2" />
              {t('teaching.colles.documents')}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </motion.div>

          {/* 2022-2023 */}
          <motion.div 
            className="rounded-2xl border border-[#d7d0d9] bg-[linear-gradient(135deg,rgba(250,247,250,0.95),rgba(240,232,240,0.92))] p-8 shadow-xl hover:shadow-2xl transition-all duration-300"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 1.0 }}
            whileHover={{ scale: 1.02 }}
          >
            <div className="flex items-center mb-6">
              <Calendar className="w-8 h-8 text-[#8a6f88] mr-3" />
              <h3 className="font-display text-3xl font-semibold text-stone-800">2022-2023</h3>
            </div>
            <p className="text-stone-600 mb-6">
              {t('teaching.colles.academicYear')} 2022-2023
            </p>
            <Link 
              to="/teaching/2022-2023"
              className="inline-flex items-center px-6 py-3 bg-[#8a6f88] text-white rounded-lg hover:bg-[#735d78] transition-colors duration-200 font-medium"
            >
              <BookOpen className="w-5 h-5 mr-2" />
              {t('teaching.colles.documents')}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </motion.div>
        </div>

        {/* Contact Section */}
        <motion.div 
          className="rounded-2xl border border-[#d7d0d9] bg-[linear-gradient(135deg,rgba(250,247,250,0.95),rgba(240,232,240,0.92))] p-8 text-center shadow-xl"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.2 }}
        >
          <div className="flex items-center justify-center mb-4">
            <Mail className="w-8 h-8 text-[#735d78] mr-3" />
            <h3 className="font-display text-3xl font-semibold text-stone-800">{t('teaching.colles.contact')}</h3>
          </div>
          <p className="text-stone-600 mb-4">
            {t('teaching.colles.contactDesc')}
          </p>
          <a 
            href="mailto:oussama.zekri@ens-paris-saclay.fr"
            className="text-[#735d78] hover:text-[#614c66] font-medium text-lg transition-colors duration-200"
          >
            oussama.zekri@ens-paris-saclay.fr
          </a>
        </motion.div>
      </motion.div>


    </motion.div>
  );
};

export default Teaching; 
